import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';
import { FONTS, COLORS } from '../../utils/styleConstants';

const EditorContainer = styled.div`
  .ql-editor {
    font-family: ${FONTS.SECONDARY};
    font-size: 0.9rem;
    line-height: 1.5;
    min-height: ${props => props.minHeight || '150px'};
    color: #333;
    padding: 1rem;
    border-radius: 0 0 1rem 1rem;
  }

  .ql-editor.ql-blank::before {
    color: #a0aec0;
    font-style: italic;
    font-family: ${FONTS.SECONDARY};
    font-size: 0.9rem;
  }

  .ql-toolbar {
    border: 1px solid #75a9f9;
    border-bottom: none;
    border-radius: 1rem 1rem 0 0;
    background-color: #f8f9fa;
    padding: 0.5rem;
  }

  .ql-container {
    border: 1px solid #75a9f9;
    border-top: none;
    border-radius: 0 0 1rem 1rem;
    font-family: ${FONTS.SECONDARY};
  }

  .ql-editor:focus {
    outline: none;
  }

  .ql-toolbar .ql-stroke {
    fill: none;
    stroke: #5a94f5;
  }

  .ql-toolbar .ql-fill,
  .ql-toolbar .ql-stroke.ql-fill {
    fill: #5a94f5;
  }

  .ql-toolbar button:hover,
  .ql-toolbar button:focus {
    color: #5a94f5;
  }

  .ql-toolbar button.ql-active {
    color: #5a94f5;
  }

  .ql-toolbar .ql-picker-label:hover,
  .ql-toolbar .ql-picker-label.ql-active {
    color: #5a94f5;
  }

  .ql-disabled {
    .ql-toolbar {
      display: none;
    }
    
    .ql-editor {
      background-color: #f5f5f5;
      color: #666;
      cursor: not-allowed;
    }
  }

  ${props => props.readOnly && `
    .ql-toolbar {
      display: none;
    }
    
    .ql-container {
      border: 1px solid #ddd;
      border-radius: 1rem;
    }
    
    .ql-editor {
      background-color: #f5f5f5;
      color: #666;
      cursor: default;
      border: none;
      border-radius: 1rem;
    }
  `}

  /* Estilos para el contenido renderizado */
  .ql-editor h1, .ql-editor h2, .ql-editor h3 {
    font-family: ${FONTS.PRIMARY};
    color: ${COLORS.SECONDARY};
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  .ql-editor h1 {
    font-size: 1.5rem;
    font-weight: 600;
  }

  .ql-editor h2 {
    font-size: 1.3rem;
    font-weight: 600;
  }

  .ql-editor h3 {
    font-size: 1.1rem;
    font-weight: 600;
  }

  .ql-editor strong {
    font-weight: 600;
  }

  .ql-editor ul, .ql-editor ol {
    padding-left: 1.5rem;
  }

  .ql-editor li {
    margin-bottom: 0.25rem;
  }

  .ql-editor a {
    color: ${COLORS.PRIMARY};
    text-decoration: none;
  }

  .ql-editor a:hover {
    text-decoration: underline;
  }
`;

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Escribe aquí...',
  readOnly = false,
  disabled = false,
  minHeight = '150px',
  id,
  style = {}
}) => {
  // Configuración de la barra de herramientas
  const modules = {
    toolbar: readOnly ? false : [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align',
    'link'
  ];

  const handleChange = (content, delta, source, editor) => {
    if (onChange && !readOnly && !disabled) {
      // Pasar el contenido HTML al componente padre
      onChange(content);
    }
  };

  return (
    <EditorContainer 
      minHeight={minHeight} 
      readOnly={readOnly}
      style={style}
    >
      <ReactQuill
        id={id}
        value={value || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly || disabled}
        theme="snow"
      />
    </EditorContainer>
  );
};

export default RichTextEditor; 